// FE → BE izin eşlemesi (RBAC tek doğruluk kaynağı = backend).
//
// FE tarihsel olarak ince granülerli izin anahtarları kullanır; backend ise kaba
// policy seti zorlar (`content.write`, `security.manage`, …). Gerçek yetki kararı
// her zaman BE'nin token'a bastığı `permission` claim'lerinden gelir — bu tablo
// yalnızca her FE-niyet anahtarının HANGİ BE policy'sine karşılık geldiğini söyler.
// Tablo, BE controller'larındaki gerçek `[Authorize(Policy = …)]` attribute'larından
// türetilmiştir.
//
// NOT: İçerik controller'larında admin GET'leri bile `content.write` ister
// (`content.read` admin uçlarında kullanılmaz) — bu yüzden içerik `*.view`
// anahtarları `content.write`'a eşlenir.
//
// Drift koruması: `permissionMap.test.ts` BE_PERMISSIONS'ı BE `Permissions.All`
// kataloğuyla iki yönlü karşılaştırır (yerelde canlı Permissions.cs'e karşı da
// doğrular) ve her FE_TO_BE hedefinin bu katalogda bulunduğunu kontrol eder.
import { PERMISSIONS, type Permission } from './permissions.const';

/**
 * Backend `Permissions.All` aynası — parite testinin referansı.
 * `EmlakKatilim.Cms.Domain/Identity/Permissions.cs` ile birebir aynı sıradadır.
 */
export const BE_PERMISSIONS = [
  'content.read',
  'content.write',
  'content.publish',
  'content.delete',
  'content.customcode',
  'taxonomy.manage',
  'translations.manage',
  'kvkk.unmask',
  'users.manage',
  'roles.manage',
  'users.activity.read',
  'kvkk.manage',
  'kvkk.force',
  'settings.manage',
  'settings.read',
  'audit.read',
  'audit.export',
  'media.manage',
  'security.manage',
  'web.manage',
  'redirect.manage',
  'dashboard.read',
  'tasks.read',
  'tasks.write',
  'files.read',
  'files.write',
  'files.delete',
  'form.view',
  'form.manage',
  'form.submissions',
  'branch.view',
  'branch.manage',
  'project.manage',
  'components.manage',
] as const;

export type BePermission = (typeof BE_PERMISSIONS)[number];

const P = PERMISSIONS;

/** Her FE izin anahtarı → onu karşılayan BE policy. */
export const FE_TO_BE: Record<Permission, BePermission> = {
  // --- İçerik: admin okuma+yazma = content.write, yayın işleri = content.publish ---
  [P.ARTICLE_VIEW]: 'content.write',
  [P.ARTICLE_CREATE]: 'content.write',
  [P.ARTICLE_UPDATE_OWN]: 'content.write',
  [P.ARTICLE_UPDATE_ANY]: 'content.write',
  [P.ARTICLE_DELETE]: 'content.write',
  [P.ARTICLE_PUBLISH]: 'content.publish',
  [P.ARTICLE_ARCHIVE]: 'content.publish',
  [P.ARTICLE_RESTORE_VERSION]: 'content.publish',
  [P.ARTICLE_FEATURED]: 'content.publish',

  [P.SSS_VIEW]: 'content.write',
  [P.SSS_CREATE]: 'content.write',
  [P.SSS_UPDATE]: 'content.write',
  [P.SSS_DELETE]: 'content.write',
  [P.SSS_PUBLISH]: 'content.publish',
  [P.SSS_RESTORE_VERSION]: 'content.publish',

  [P.PAGE_VIEW]: 'content.write',
  [P.PAGE_CREATE]: 'content.write',
  [P.PAGE_UPDATE]: 'content.write',
  [P.PAGE_DELETE]: 'content.write',
  [P.PAGE_PUBLISH]: 'content.publish',

  // --- Kampanya (Article ile aynı content.* eşlemesi) ---
  [P.CAMPAIGN_VIEW]: 'content.write',
  [P.CAMPAIGN_CREATE]: 'content.write',
  [P.CAMPAIGN_UPDATE]: 'content.write',
  [P.CAMPAIGN_DELETE]: 'content.write',
  [P.CAMPAIGN_PUBLISH]: 'content.publish',
  [P.CAMPAIGN_ARCHIVE]: 'content.publish',
  [P.CAMPAIGN_RESTORE_VERSION]: 'content.publish',

  // Workflow onay kuyruğu — cross-type editör görünümü (content.write yeter).
  [P.WORKFLOW_QUEUE_VIEW]: 'content.write',

  // K-6 — CustomCode alanı yazımı; content.write'ı DEĞİL, kendi dar iznini ister.
  [P.CONTENT_CUSTOM_CODE]: 'content.customcode',

  // --- Web mikro-site ---
  [P.WEB_REDIRECT]: 'redirect.manage',
  [P.WEB_OG_PREVIEW]: 'web.manage',

  // --- Config-tier site bileşenleri → tek `web.manage` policy'si ---
  [P.MENU_MANAGE]: 'web.manage',
  [P.SLIDER_MANAGE]: 'web.manage',
  [P.BANNER_MANAGE]: 'web.manage',
  [P.POPUP_MANAGE]: 'web.manage',

  // --- Kullanıcılar / KVKK / roller ---
  [P.KVKK_DELETION_MANAGE]: 'kvkk.manage',
  [P.KVKK_UNMASK]: 'kvkk.unmask',
  [P.USERS_MANAGE]: 'users.manage',
  [P.ADMIN_USER_MANAGE]: 'users.manage',
  [P.ROLE_MANAGE]: 'roles.manage',

  // --- Dashboard + dosyalar ---
  [P.DASHBOARD_VIEW]: 'dashboard.read',
  [P.FILES_READ]: 'files.read',
  [P.FILES_WRITE]: 'files.write',
  [P.FILES_DELETE]: 'files.delete',

  // --- Formlar (MOD-07): tanım okuma/yazma + PII başvuru erişimi ayrı izni ---
  [P.FORM_VIEW]: 'form.view',
  [P.FORM_MANAGE]: 'form.manage',
  [P.FORM_SUBMISSIONS]: 'form.submissions',

  // --- Şubeler (MOD-11): okuma + CRUD yönetimi ayrı izinler ---
  [P.BRANCH_VIEW]: 'branch.view',
  [P.BRANCH_MANAGE]: 'branch.manage',

  // --- Denetim + taksonomi + sistem ---
  [P.AUDIT_LOG_VIEW]: 'audit.read',
  [P.CATEGORY_MANAGE]: 'taxonomy.manage',
  [P.TAG_MANAGE]: 'taxonomy.manage',
  [P.AUTHOR_MANAGE]: 'taxonomy.manage',
  [P.TRANSLATION_MANAGE]: 'translations.manage',
  [P.SETTINGS_MANAGE]: 'settings.manage',

  // --- Güvenlik yönetimi (MOD-04): IP allowlist CRUD + 2FA reset gate'i ---
  [P.SECURITY_MANAGE]: 'security.manage',

  // --- Çoklu-site (REQ-45): proje CRUD + kullanıcı↔proje atama ---
  [P.PROJECT_MANAGE]: 'project.manage',

  // --- Component Studio (MOD-17): bileşen tanımı yazımı + yayın/arşiv/versiyon ---
  [P.COMPONENT_MANAGE]: 'components.manage',
};

/** Backend'in her şeyi geçtiği rol (PermissionAuthorizationHandler ile aynı). */
const SUPER_ADMIN_ROLE = 'superadmin';

export function isSuperAdmin(roles: readonly string[] | undefined | null): boolean {
  return (roles ?? []).some((r) => r.toLowerCase() === SUPER_ADMIN_ROLE);
}

/**
 * Bir FE izin anahtarının, kullanıcının BE claim'lerine göre verilip
 * verilmediğini çözer. SuperAdmin tüm izinleri geçer.
 */
export function hasPermission(
  permission: Permission,
  grantedBePermissions: readonly string[] | undefined | null,
  roles: readonly string[] | undefined | null,
): boolean {
  if (isSuperAdmin(roles)) return true;
  const required = FE_TO_BE[permission];
  if (!required) return false;
  return (grantedBePermissions ?? []).includes(required);
}
